import api from "./api";

// Types for GET /api/admin/dashboard/retention and /retention/wallets.
// Weeks run Monday-Sunday UTC; dates are YYYY-MM-DD; rates are 0-1 or null.

export type RetentionPlatform = "all" | "gmx" | "hyperliquid";
export type RetentionStatus = "new" | "retained" | "returning" | "churned";

export interface RetentionWeek {
    weekStart: string;
    weekEnd: string;
    startMs: number;
    endMs: number;
    isComplete: boolean;
    isBaseline: boolean;
    activeTraders: number;
    newTraders: number;
    retainedTraders: number;
    returningTraders: number;
    churnedTraders: number;
    prevActiveTraders: number;
    retentionRate: number | null;
    trades: number;
    volume: number;
}

export interface RetentionCohort {
    cohortWeekStart: string;
    size: number;
    isBaseline: boolean;
    retention: { weekOffset: number; retained: number; rate: number | null }[];
}

export interface RetentionDiagnostics {
    invalidTimestampRows: number;
    unmappedGmxWallets: number;
    missingWalletRows: number;
    identityConflicts: number;
    duplicateOrderRows: number;
    hlFillsFromOwnWallet: number;
    excludedWallets: number;
}

export interface RetentionData {
    weekStartsOn: "monday";
    timezone: "UTC";
    platform: RetentionPlatform;
    dataSince: string | null;
    dataAsOf: string;
    generatedAt: string;
    summary: {
        lastCompleteWeek: RetentionWeek | null;
        previousCompleteWeek: RetentionWeek | null;
    };
    weeks: RetentionWeek[];
    cohorts: RetentionCohort[];
    diagnostics: RetentionDiagnostics;
}

export interface RetentionWalletRow {
    address: string;
    firstTradeWeek: string;
    thisWeek: { trades: number; volume: number };
    prevWeek: { trades: number; volume: number };
    lastTradeWeek: string | null;
    nextTradeWeek: string | null;
}

export interface RetentionWalletsData {
    weekStart: string;
    weekEnd: string;
    isComplete: boolean;
    isBaseline: boolean;
    status: RetentionStatus;
    platform: RetentionPlatform;
    total: number;
    page: number;
    limit: number;
    wallets: RetentionWalletRow[];
}

export async function fetchRetention(params: {
    weeks: number;
    platform: RetentionPlatform;
}): Promise<RetentionData> {
    const res = await api.get("/api/admin/dashboard/retention", { params });
    return res.data.data;
}

export async function fetchRetentionWallets(params: {
    weekStart: string;
    status: RetentionStatus;
    platform: RetentionPlatform;
    page: number;
    limit: number;
}): Promise<RetentionWalletsData> {
    const res = await api.get("/api/admin/dashboard/retention/wallets", { params });
    return res.data.data;
}
